/**
 * Converts data url to file object
 * @param {String} dataUrl Dataurl text
 * @param {String} fileName FileName
 * @return {File|null} File Object
 */

import { getSearchPDP, getProjectionData } from "@/services/ct.service.js"
import { getUserPersona } from "@/utils/helper"
import CONSTANTS from "@/constants/index"

/**
 *
 * @param {*} dataUrl
 * @param {*} fileName
 * @return {File}
 */
export async function dataUrlToFile(dataUrl, fileName) {
  if (dataUrl) {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    return new File([blob], fileName, { type: "image/png" })
  }
  return null
}

/**
 * Crops canvas and return new canvas
 * @param {Canvas} sourceCanvas Canvas to be cropped
 * @param {Number} left left coordinate
 * @param {Number} top top coordinate
 * @param {Number} width Width of crop
 * @param {Number} height Height of the crop
 * @return {Canvas} Canvas
 */
export const cropCanvas = (sourceCanvas, left, top, width, height) => {
  const destCanvas = document.createElement("canvas")
  destCanvas.width = width
  destCanvas.height = height
  destCanvas
    .getContext("2d")
    .drawImage(sourceCanvas, left, top, width, height, 0, 0, width, height)
  return destCanvas
}

/**
 * Converts to cropped image
 * @param {Image} image
 * @param {Object} crop
 * @param {String} fileName
 * @param {String} type
 * @return {File}
 */
export function getCroppedImg(image, crop, fileName, type) {
  const canvas = document.createElement("canvas")
  const scaleX = image?.naturalWidth ? image.naturalWidth / image.width : 1
  const scaleY = image?.naturalHeight ? image.naturalHeight / image.height : 1
  canvas.width = crop.width
  canvas.height = crop.height
  const ctx = canvas.getContext("2d")

  const pixelRatio = window.devicePixelRatio
  canvas.width = crop.width * pixelRatio
  canvas.height = crop.height * pixelRatio
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  ctx.imageSmoothingQuality = "high"

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height
  )

  const base64Image = canvas.toDataURL(type)
  return dataUrlToFile(base64Image, fileName)
}

export const debounce = (func, debounceTime) => {
  let debounce = true
  return () => {
    if (debounce) {
      debounce = false
      setTimeout(() => {
        func()
        debounce = true
      }, debounceTime)
    }
  }
}

const promiseImageFileReader = imageFile => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => {
      resolve({ result: e.target.result, readerResult: reader.result })
    }
    reader.onerror = e => {
      reject(e)
    }
    reader.readAsDataURL(imageFile)
  })
}

const promiseImageReader = source => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      resolve(image)
    }
    image.onerror = e => {
      reject(e)
    }
    image.src = source
  })
}

const imageExtentionCheck = imageFile => {
  if (
    imageFile &&
    imageFile.name?.toLowerCase()?.match(/\.(jpg|jpeg|heic|heif|png|bmp|jfif)$/)
  )
    return true
  return false
}

export const validateImageFileAsync = async imageFile => {
  try {
    if (imageExtentionCheck(imageFile)) {
      const { result, readerResult } = await promiseImageFileReader(imageFile)
      const image = await promiseImageReader(result)
      return {
        width: image.width,
        height: image.height,
        image: image,
        file: imageFile,
        dataURL: readerResult,
      }
    }
  } catch (e) {
    return false
  }
  return false
}

/**
 *
 * @param {*} value
 * @returns
 */

export const getValidCategory = value => {
  try {
    let categoryList = [
      "faucets",
      "toilet",
      "valve",
      "shower",
      "servicepart-faucet",
      "servicepart-toilet",
      "servicepart-shower",
    ]
    if (value && value.toLowerCase() === "toilet") {
      value = "toilet_group"
      categoryList = [
        "faucets",
        "toilet_group",
        "valve",
        "shower",
        "servicepart-faucet",
        "servicepart-toilet",
        "servicepart-shower",
      ]
    }
    if (categoryList.includes(value.toLowerCase())) {
      return value.toLowerCase()
    } else {
      return categoryList[0]
    }
  } catch (e) {
    console.log(e, "error in getValidCategory")
  }
}

/**
 * Retrieves detailed SKU information for a product.
 * @param {String} slug - The product slug.
 * @param {String} skuId - The SKU ID to retrieve details for.
 * @return {Promise<Object>} A promise that resolves to an object containing SKU details.
 */
export const getDetailSku = async (slug, skuId) => {
  try {
    const resp = await getSearchPDP(slug, false)
    const productImages =
      resp?.data?.response?.docs?.[0]["productImages.labelWithUrl_ss"]
    const colorDetails = resp?.data?.response?.docs?.[0]["Color.SKU.Details_ss"]
    const skus = colorDetails.map(item => item.split("|")[2])
    const response = await getProjectionData({ slug, skus })
    const princeInfo = getPartsPricebySku(response)
    const priceArray = princeInfo[skuId]
    const productArray = productImages
      .filter(image => {
        const [, , type] = image.split("|")
        return type === "IMGITEMISO"
      })
      .map(image => {
        const [sku, , , , , imageUrl] = image.split("|")
        const colorDetail = colorDetails.find(detail => detail.includes(sku))
        if (colorDetail) {
          const [label, colorUrl, id, , , value] = colorDetail.split("|")
          return {
            sku,
            id,
            imageUrl,
            label,
            colorUrl: `${CONSTANTS.COLOR_FILE_URL}${colorUrl}`,
            value,
          }
        }
        return null
      })
      .filter(item => item !== null)

    const mapArrayWithPrices = (productArray, priceArray) => {
      return productArray.map(product => {
        const priceInfo =
          priceArray.find(price => price.sku === product.sku) || {}
        return {
          ...product,
          prices: priceInfo,
        }
      })
    }
    const mappedArray = mapArrayWithPrices(productArray, priceArray)
    return mappedArray
  } catch (err) {
    console.error("Error in getDetailSku", err)
  }
}

/**
 * Maps a list of products to their prices by SKU.
 * @param {Array} list - The list of products.
 * @param {String} [currencyCode="USD"] - The currency code to use.
 * @param {String} [currencySign="$"] - The currency sign to use.
 * @return {Object} An object mapping SKUs to their price details.
 */
export const getPartsPricebySku = (
  list,
  currencyCode = "USD",
  currencySign = "$"
) => {
  const sortItems = i => ({
    sku: i?.sku,
    currencySign,
    discountPrices: i?.prices
      .filter(i => {
        return i?.discounted?.value?.currencyCode === currencyCode
      })
      .reduce((a, i) => {
        return {
          ...a,
          [i?.customerGroup?.obj?.key ?? "ANY"]: i?.discounted?.value,
        }
      }, {}),
    prices: i?.prices
      .filter(i => {
        return i?.value?.currencyCode === currencyCode
      })
      .reduce((a, i) => {
        return { ...a, [i?.customerGroup?.obj?.key ?? "ANY"]: i?.value }
      }, {}),
  })
  return list.reduce((acc, sku) => {
    return {
      ...acc,
      [sku?.masterVariant?.sku]: [
        ...sku.variants.map(sortItems),
        ...[sku.masterVariant].map(sortItems),
      ],
    }
  }, {})
}

/**
 * Formats a price value into a string.
 * @param {Number} price - The price value to format.
 * @return {String|null} The formatted price string or null if the price is invalid.
 */
export const formatPrice = price => {
  return price ? `$${Number((price ?? 0) / 100).toFixed(2)}` : null
}

/**
 * Retrieves the actual and strike-through prices for a product.
 * @param {Number} priceCTR - The original price.
 * @param {Number} discountPrice - The discounted price.
 * @return {Object} An object containing the actual price and strike-through price.
 */
export const getPrice = (priceCTR, discountPrice) => {
  const price = {}
  if (discountPrice) {
    price.actualPrice = formatPrice(discountPrice)
    price.strikePrice = formatPrice(priceCTR)
  } else {
    price.actualPrice = formatPrice(priceCTR)
    price.strikePrice = null
  }
  return price
}

/**
 * Extracts and maps parts file information from product data.
 * @param {Array} data - The product data containing parts information.
 * @return {Array|null} An array of objects containing part IDs and titles, or null if an error occurs.
 */

export const getPartsFileMap = data => {
  try {
    const extract1 = data?.[0]?.masterVariant?.attributes?.filter(
      item => item.name === "ProductResource"
    )?.[0]?.value
    const extract2 = extract1.filter(
      item =>
        item.filter(
          i => i.name === "ResourceType" && i.value === "InteractiveSVG"
        ).length > 0
    )
    const extract3 = extract2.map(item => {
      const obj = {}
      item.forEach(item => {
        obj[item.name] = item.value
      })
      return obj
    })
    const extract4 = extract3.reduce((acc, item) => {
      acc.push({ id: item?.ResourceName, title: item.ResourceTitle })
      return acc
    }, [])
    return extract4
  } catch (e) {
    console.error("Error in getPartsFileMap:", e)
    return null
  }
}

/**
 * Opens a product detail page (PDP) in a new browser tab.
 * @param {String} url - The URL to open.
 */

export const viewPdp = url => {
  const urlObj = new URL(url)
  const path = urlObj.pathname + urlObj.search
  const fullUrl = `${window.origin}${path}`
  window.open(fullUrl, "_blank")
}

/**
 * Checks if an image URL is valid and accessible.
 * @param {String} url - The image URL to check.
 * @return {Promise<Boolean>} A promise that resolves to true if the image exists, otherwise false.
 */
export const checkImageUrl = async url => {
  try {
    const response = await fetch(url)
    if (response.ok) return response.ok
  } catch {
    return false
  }
}

/**
 * Retrieves service parts information for a product.
 * @param {Object} partList - The part list object.
 * @param {String} productId - The product ID.
 * @returns {Promise<Object>} A promise that resolves to the service parts information.
 */

export const getServiceParts = async (partList, productId) => {
  const partDiagramtext = "text for parts"
  try {
    const projectionData = await getProjectionData({
      slug: partList?.Info?.slug,
      skus: [productId],
    })
    const fileMapItems = getPartsFileMap(projectionData)
    const partNames = await getSearchPDP(partList?.Info?.slug)
    let fileMap = fileMapItems
    if (!fileMapItems) {
      fileMap = partList?.SVGFiles?.map((file, index) => {
        return {
          title: `${partDiagramtext} ${index + 1}`,
          id: file.FileName,
        }
      })
    }
    const product = {
      productId: partList?.Info?.prodId,
      partId: partList?.SVGFiles?.[0]?.FileName,
      analyticsCustomData: "",
      num: partNames?.data?.response?.numFound,
      fileMap,
    }
    return product
  } catch (e) {
    console.error("Error in getServiceParts:", e)
  }
}

/**
 * Scrolls smoothly to a specified section in the document.
 * @param {String} divSection - The class name of the section to scroll to.
 */

export const getScrollTo = divSection => {
  const detailsText = document.querySelector(`.${divSection}`)
  if (detailsText) {
    detailsText.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    })
  }
}

/**
 * Retrieves the persona-specific price detail from a price list.
 * @param {Object} priceDtl - The price detail object.
 * @param {String} [currencyCode="USD"] - The currency code to use.
 * @returns {Number|undefined} The persona-specific price or undefined if not found.
 */

export const getPersonaPriceListDetail = (priceDtl, currencyCode = "USD") => {
  const persona = getUserPersona()
  let filerPrice
  if (priceDtl?.prices) {
    filerPrice = (priceDtl?.prices || []).filter(price => {
      const priceDiscounted = price?.discounted?.value
      const personaPriceDetails = price.customerGroup?.obj?.key === persona
      const priceUSD = price?.value?.currencyCode === currencyCode
      if (
        priceDiscounted &&
        price.discounted.value.currencyCode === currencyCode
      ) {
        return price.customerGroup?.obj?.key === persona
      } else if (personaPriceDetails && priceUSD && !priceDiscounted) {
        return true
      } else if (
        (!price.customerGroup?.obj?.key || !personaPriceDetails) &&
        price?.key?.includes("ANY") &&
        priceUSD
      ) {
        return true
      }
      return false
    })
  }

  const dicountedPerson =
    filerPrice?.[0]?.discounted?.value?.centAmount ??
    filerPrice?.[1]?.value?.centAmount
  filerPrice = dicountedPerson ?? filerPrice?.[0]?.value?.centAmount

  return filerPrice
}

/**
 * Checks whether a given URL points to a valid, loadable image. *
 * @param {string} url - The URL to test.
 * @returns {Promise<boolean>} Resolves to true if the URL loads as an image,otherwise false.
 */

export const isImageUrl = url => {
  return new Promise(resolve => {
    if (!url) return resolve(false)

    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })
}
